import { useEffect, useState } from "react";
import { Row, Col, OverlayTrigger, Popover } from "react-bootstrap";
import { useWatch } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import { ReactComponent as Exchange } from "assets/images/icons/Exchange.svg";
import { getExchangeRates, revertExchangeRate } from "api";
import ConversionRate from "components/organisms/ConversionRate/ConversionRate";
import errorToast from "helpers/errorToast";
import { formatPriceV2 } from "helpers/commonHelpers";
import CurrencyExchangeRateDialog from "./CurrencyExchangeRateDialog";
import "../invoiceFormCosts.scss";

function TotalCost({ isCustomBroker }: { isCustomBroker: boolean }) {
  const [searchParams] = useSearchParams();
  const currentInvoiceId: string | null = searchParams.get("invoice");
  const data = useWatch({ name: "shipment" });
  const costData = useWatch({ name: "costs" });
  const { total_quoted_amount, total_quoted_amount_currency, total_invoiced_amount, total_invoiced_amount_currency } =
    data || {};

  const [totalCosts, setTotalCosts] = useState<any[]>([]);
  const [currentInvoiceTotal, setCurrentInvoiceTotal] = useState<number>(0);
  const [invoicedTotal, setInvoicedTotal] = useState<number>(0);
  const [calculatingTotalCosts, setCalculatingTotalCosts] = useState<boolean>(false);
  const [conversionRates, setConversionRates] = useState<any[]>([]);
  const [quotedCurrencies, setQuotedCurrencies] = useState<string[]>([]);
  const [quotedCurrenciesConversions, setQuotedCurrenciesConversions] = useState<any[]>([]);
  const [exchangeRateCurrency, setExchangeRateCurrency] = useState<string | null>(null);
  const [showExchangeRateDialog, setShowExchangeRateDialog] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      combineCosts();
    }, 500);
    return () => clearTimeout(timer);
  }, [costData]);

  // Get conversion rates for each unique currency for both quoted and invoiced costs
  useEffect(() => {
    if (!totalCosts || !quotedCurrencies) return;
    getCurrencyConversions();
  }, [totalCosts, quotedCurrencies]);

  useEffect(() => {
    calculateTotalCost();
  }, [totalCosts, conversionRates, costData]);

  const refetchManualCurrency = (currency: string | null, isRevert: boolean) => {
    setCalculatingTotalCosts(true);
    getExchangeRates(currentInvoiceId, {
      params: {
        currency,
        new_currency: total_invoiced_amount_currency,
        value: 1,
      },
    })
      .then((res) => {
        const index = conversionRates.findIndex(
          (obj) => obj.currency === res.data.currency && obj.new_currency === res.data.new_currency,
        );
        if (index !== -1) {
          const updatedRates = [...conversionRates];
          updatedRates[index] = { ...updatedRates[index], ...res.data, manually_updated: !isRevert };
          setConversionRates(updatedRates);
        }
      })
      .catch((err) => errorToast(err))
      .finally(() => setCalculatingTotalCosts(false));
  };

  const getCurrencyConversions = () => {
    setCalculatingTotalCosts(true);
    const newInvoicedCurrencies = totalCosts.filter(
      (cost) => !conversionRates.some((rate) => rate.currency === cost.currency),
    );
    const newQuotedCurrencies = quotedCurrencies.filter(
      (currency) => !quotedCurrenciesConversions.some((rate) => rate.currency === currency),
    );

    if (newInvoicedCurrencies.length === 0 && newQuotedCurrencies.length === 0) {
      setCalculatingTotalCosts(false);
      return; // No new currencies to fetch
    }

    const invoicedRates = newInvoicedCurrencies.map((cost) =>
      getExchangeRates(currentInvoiceId, {
        params: {
          currency: cost.currency,
          new_currency: total_invoiced_amount_currency,
          value: cost.totalCost,
        },
      }),
    );

    const quotedRates = newQuotedCurrencies.map((currency) =>
      getExchangeRates(currentInvoiceId, {
        params: {
          currency,
          new_currency: total_invoiced_amount_currency,
          value: 1,
        },
      }),
    );

    Promise.all([...invoicedRates, ...quotedRates])
      .then((res) => {
        const formattedInvoicedData = res
          .filter((item) => newInvoicedCurrencies.some((cost) => cost.currency === item.data.currency))
          .map((item) => ({
            currency: item.data.currency,
            new_currency: item.data.new_currency,
            conversion_rate: item.data.conversion_rate,
            manually_updated: item.data.manually_updated,
          }));

        const formattedQuotedData = res
          .filter((item) => newQuotedCurrencies.includes(item.data.currency))
          .map((item) => ({
            currency: item.data.currency,
            new_currency: item.data.new_currency,
            conversion_rate: item.data.conversion_rate,
            manually_updated: item.data.manually_updated,
          }));

        setConversionRates((prevRates) => [...prevRates, ...formattedInvoicedData]);
        setQuotedCurrenciesConversions(() => [...formattedQuotedData]);
      })
      .catch((err) => errorToast(err))
      .finally(() => setCalculatingTotalCosts(false));
  };

  // Combine costs off all tables, grouped by unique currencies
  const combineCosts = () => {
    if (!costData) return;

    const combinedCosts: any = {};
    const quotedCurrencies: string[] = [];

    Object.keys(costData).forEach((key) => {
      if (!Array.isArray(costData[key])) return;

      costData[key].forEach((item: any) => {
        const { invoice_cost_currency_code, quoted_amount_currency_code, invoiced_cost, type } = item;
        if (!invoiced_cost) return;

        if (invoice_cost_currency_code) {
          if (!combinedCosts[invoice_cost_currency_code]) {
            combinedCosts[invoice_cost_currency_code] = 0;
          }
          combinedCosts[invoice_cost_currency_code] += (type === 2 ? -1 : 1) * parseFloat(invoiced_cost);
        }

        if (quoted_amount_currency_code && !quotedCurrencies.includes(quoted_amount_currency_code)) {
          quotedCurrencies.push(quoted_amount_currency_code);
        }
      });
    });

    // Convert combined costs object to array of objects
    const result = Object.keys(combinedCosts).map((currency) => ({
      currency,
      totalCost: combinedCosts[currency],
    }));

    setTotalCosts(result);
    setQuotedCurrencies(quotedCurrencies);
  };

  // Take all converted costs of unique currencies and calculate the total invoiced amount,
  // rounding UP to 2 decimal places
  const calculateTotalCost = () => {
    const roundUpToTwoDecimals = (num: number) => Math.ceil(num * 100) / 100;
    const totalConvertedCost = totalCosts
      .map((cost: { currency: string; totalCost: number }) => {
        const conversionRate = conversionRates.find((rate) => rate.currency === cost.currency);
        if (conversionRate) {
          // Convert and round up to 2 decimal places
          const convertedCost = cost.totalCost * parseFloat(conversionRate.conversion_rate);
          return roundUpToTwoDecimals(convertedCost);
        }
        return null;
      })
      .filter((cost) => cost !== null)
      .reduce((accumulator, currentValue: any) => accumulator + currentValue, 0);
    setCurrentInvoiceTotal(totalConvertedCost ?? 0);
    setInvoicedTotal(totalConvertedCost + total_invoiced_amount);
  };

  const handleEditExchangeRate = (currency: string) => {
    setExchangeRateCurrency(currency);
    setShowExchangeRateDialog(true);
  };

  const handleRevertExchangeRate = (currency: string) => {
    setCalculatingTotalCosts(true);
    revertExchangeRate(currentInvoiceId, {
      params: { currency },
    }).then(() => refetchManualCurrency(currency, true));
  };

  const selectedCurrencies = new Set(totalCosts.map((item) => item.currency));
  const filteredConversionRates = conversionRates.filter((rate) => selectedCurrencies.has(rate.currency));

  return (
    <Row>
      <CurrencyExchangeRateDialog
        currency={exchangeRateCurrency}
        targetCurrency={total_invoiced_amount_currency}
        setExchangeRateCurrency={setExchangeRateCurrency}
        show={showExchangeRateDialog}
        close={() => setShowExchangeRateDialog(false)}
        invoiceId={currentInvoiceId}
        refetch={refetchManualCurrency}
      />
      <Col xs={3}>
        <h6>
          <FormattedMessage id="quotation-form.total" defaultMessage="Total" />
        </h6>
      </Col>
      {isCustomBroker ? (
        <Col />
      ) : (
        <Col>
          <div>
            <FormattedMessage id="billing.quotedAmount" defaultMessage="Quoted amount" />
          </div>
          <h6 className="mt-md">
            {formatPriceV2(total_quoted_amount)} {total_quoted_amount_currency}
          </h6>
          <div>
            {quotedCurrenciesConversions.map((cost, index) => (
              <div key={index} className="text-secondary font-b3">
                <FormattedMessage id="quotation-form.conversionRate" defaultMessage="Conversion rate" /> {cost.currency}/
                {cost.new_currency}: {formatPriceV2(cost.conversion_rate, { maximumFractionDigits: 4 })}
              </div>
            ))}
          </div>
        </Col>
      )}
      <Col>
        <div>
          <FormattedMessage id="invoices.invoicedAmount" defaultMessage="Invoiced amount" />
        </div>
        {calculatingTotalCosts && (
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCircleNotch} className="text-secondary" spin />
            <div className="ml-3">
              <FormattedMessage id="test" defaultMessage="Calculating total costs" />
            </div>
          </div>
        )}

        {!calculatingTotalCosts && (
          <div>
            <h6 className="mt-md">
              {!isNaN(currentInvoiceTotal) && formatPriceV2(currentInvoiceTotal)} {total_invoiced_amount_currency}
            </h6>
            <div>
              <div>
                {filteredConversionRates.map((cost, index) => {
                  if (cost.currency === cost.new_currency) return;
                  const conversionItem = {
                    target_currency: cost.currency,
                    base_currency: cost.new_currency,
                    rate: cost.conversion_rate,
                  };
                  return (
                    <div key={index} className="d-flex align-items-center mb-sm mt-sm">
                      <ConversionRate item={conversionItem} />
                      <EditPencil onClick={() => handleEditExchangeRate(cost.currency)} className="cursor-pointer ml-md" />
                      {cost.manually_updated && (
                        <OverlayTrigger
                          trigger={["hover", "focus"]}
                          placement="top"
                          rootClose
                          overlay={
                            <Popover id="popover-basic" className="mb-3">
                              <Popover.Content>
                                <div className="font-weight-bold my-2 d-flex justify-content-center">
                                  <FormattedMessage
                                    id="invoice.revertToAutomaticConversion"
                                    defaultMessage="Revert to automatic conversion"
                                  />
                                </div>
                              </Popover.Content>
                            </Popover>
                          }
                        >
                          <div className="ml-md">
                            <Exchange className="cursor-pointer" onClick={() => handleRevertExchangeRate(cost.currency)} />
                          </div>
                        </OverlayTrigger>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="d-flex align-items-center mt-sm">
              <p className="font-b1 text-base-grey">
                <FormattedMessage id="billing.totalShipmentCharges" defaultMessage="Total shipment charges" />
              </p>
              <h6 className="ml-lg mb-none">
                {!isNaN(invoicedTotal) && formatPriceV2(invoicedTotal)} {total_invoiced_amount_currency}
              </h6>
            </div>
          </div>
        )}
      </Col>
    </Row>
  );
}

export default TotalCost;
